import React, { useState, useEffect, useCallback } from 'react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import { atomOneDark, atomOneLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { FlattenedInvoiceData } from '../../types/invoice';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import LoadingOverlay from './shared/LoadingOverlay';
import UploadSection from './shared/UploadSection';
import Stepper from './shared/Stepper';
import Button from '../shared/Button';
import { scrollbarClass } from './shared/constants';
import { XMLParser } from 'fast-xml-parser';
import InvoiceViewer from './InvoiceViewer';
import { transformInvoiceData } from '../../config/invoiceFields';
import { useTranslation } from '../../hooks/useTranslation';
import { useDropzone } from 'react-dropzone';
import { trackEvent } from '../../utils/analytics';
import SEOContent from '../common/SEOContent';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

// Register XML language for syntax highlighting
SyntaxHighlighter.registerLanguage('xml', xml);

const steps: string[] = [
    'xrechnungViewer.steps.upload',
    'xrechnungViewer.steps.viewData'
];

function findInvoiceRoot(result: any) {
    if (result.Invoice) return result.Invoice;
    if (result['ubl:Invoice']) return result['ubl:Invoice'];
    if (result.CrossIndustryInvoice) return result.CrossIndustryInvoice;
    if (result['rsm:CrossIndustryInvoice']) return result['rsm:CrossIndustryInvoice'];
    return null;
}

const SimpleXRechnungViewer: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [invoiceData, setInvoiceData] = useState<FlattenedInvoiceData | null>(null);
    const [xmlContent, setXmlContent] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();
    const { theme } = useTheme();
    const [isNextLoading, setIsNextLoading] = useState(false);
    const [processingStartTime, setProcessingStartTime] = useState<number | undefined>(undefined);

    useEffect(() => {
        document.title = t('xrechnungViewer.title') + " | Invoice-Converter.com";
        return () => {
            document.title = `1-Click PDF Invoice ${t('common.to')} XRechnung Converter | Invoice-Converter.com`;
        };
    }, [t]);

    const getDynamicSyntaxHighlightingStyle = useCallback(() => {
        const isDarkMode = theme === 'dark';
        if (isDarkMode) {
            return { ...atomOneDark, hljs: { ...(atomOneDark.hljs || {}), background: 'transparent' } };
        } else {
            return { ...atomOneLight, hljs: { ...(atomOneLight.hljs || {}), background: 'transparent' } };
        }
    }, [theme]);

    const parseXmlContent = (content: string): FlattenedInvoiceData => {
        const parser = new XMLParser({
            ignoreAttributes: false,
            attributeNamePrefix: '@_',
            textNodeName: '#text',
            removeNSPrefix: true,
            parseAttributeValue: true,
            isArray: (name) => name === 'InvoiceLine'
        });

        const result = parser.parse(content);
        const invoice = findInvoiceRoot(result);

        if (!invoice) {
            throw new Error('Could not find Invoice element in XML');
        }

        return transformInvoiceData(invoice);
    };

    const handleFileChange = (files: File[]) => {
        if (files.length > 0) {
            const selectedFile = files[0];
            trackEvent('Upload', 'xml_file_selected', selectedFile.name, selectedFile.size);
            setFile(selectedFile);
        }
    };

    const onDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            trackEvent('Upload', 'xml_file_dropped', acceptedFiles[0].name, acceptedFiles[0].size);
        }
        handleFileChange(acceptedFiles);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'text/xml': ['.xml'] },
        multiple: false,
        maxSize: 10000000, // 10MB
    });

    const handleNext = async () => {
        if (activeStep === 0) {
            if (!file) {
                trackEvent('Upload', 'upload_error', 'No file selected');
                setError(t('converter.errors.selectXmlFile') || 'Please select an XML file.');
                return;
            }

            setIsNextLoading(true);
            setProcessingStartTime(Date.now());
            setError(null);

            try {
                const content = await file.text();
                setXmlContent(content);
                const parsedData = parseXmlContent(content);
                setInvoiceData(parsedData);

                trackEvent(
                    'Invoice',
                    'xml_view_success',
                    `${file.name} | anonymous`,
                    file.size
                );

                setActiveStep(1);
            } catch (err) {
                console.error('Error parsing XML:', err);
                trackEvent('Upload', 'upload_error', err instanceof Error ? err.message : 'Unknown error');
                setError(err instanceof Error ? err.message : t('converter.errors.xmlParseFailed') || 'Failed to parse the XML file.');
            } finally {
                setIsNextLoading(false);
                setProcessingStartTime(undefined);
            }
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const resetState = () => {
        // Scroll to top when starting new
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        setActiveStep(0);
        setFile(null);
        setXmlContent('');
        setInvoiceData(null);
        setError(null);
    };

    const getStepContent = (step: number): JSX.Element => {
        switch (step) {
            case 0:
                return (
                    <div className="relative">
                        <UploadSection
                            getRootProps={getRootProps}
                            getInputProps={getInputProps}
                            isDragActive={isDragActive}
                            file={file}
                            setFile={setFile}
                            fileType="xml"
                            loading={isLoading}
                            handleFileChange={handleFileChange}
                        />
                    </div>
                );
            case 1:
                return (
                    <div className="flex gap-4 h-[calc(100vh-400px)] min-h-[600px] max-h-[900px] overflow-hidden">
                        {/* XML Content - Left Side */}
                        <div className="w-[50%] bg-bg-primary rounded-xl overflow-hidden shadow-sm border border-border-primary">
                            <div className="p-6 h-full flex flex-col">
                                <h3 className="text-xl font-medium text-text-primary mb-4 flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                    </svg>
                                    {t('xrechnungViewer.xmlContent')}
                                </h3>
                                <div className={`flex-1 overflow-y-auto ${scrollbarClass} bg-bg-secondary rounded-lg p-4`}>
                                    <SyntaxHighlighter 
                                        language="xml" 
                                        style={getDynamicSyntaxHighlightingStyle()}
                                        customStyle={{
                                            background: 'transparent',
                                            fontSize: '0.9rem',
                                            borderRadius: '0.375rem',
                                            lineHeight: '1.6'
                                        }}
                                        wrapLongLines={true}
                                        showLineNumbers={true}
                                    >
                                        {xmlContent}
                                    </SyntaxHighlighter>
                                </div>
                            </div>
                        </div>
                        
                        {/* Parsed Data - Right Side */}
                        <div className="w-[50%] bg-bg-primary rounded-xl overflow-hidden shadow-sm border border-border-primary">
                            <div className="p-6 h-full flex flex-col">
                                <h3 className="text-xl font-medium text-text-primary mb-4 flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    {t('xrechnungViewer.parsedData')}
                                </h3>
                                <div className={`flex-1 overflow-y-auto ${scrollbarClass}`}>
                                    <InvoiceViewer 
                                        invoiceData={invoiceData} 
                                        readOnly={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            default:
                return <div>Unknown step</div>;
        }
    };

    const translatedSteps = steps.map(step => t(step));

    return (
        <div className="min-h-screen bg-[var(--bg-primary)]">
            <Header />
            <div className="container mx-auto px-4 pt-20">
                {/* Headlines and Subheadlines */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[var(--text-primary)] mb-2">
                        <span className="text-[var(--brand-primary)]">{t('xrechnungViewer.headline')}</span>
                    </h1>
                    <p className="text-xl text-text-secondary">
                        {t('xrechnungViewer.subtitle')}
                    </p>
                </div>

                {/* Separator Line */}
                <div className="w-full border-t border-[var(--border-primary)] mb-8"></div>

                {/* Steps Section */}
                <div className="mb-8">
                    <Stepper steps={translatedSteps} activeStep={activeStep} />
                </div>

                {/* Main Content Area */}
                <div className="mb-16">
                    {getStepContent(activeStep)}
                </div>

                {/* Navigation Buttons Container */}
                <div className="px-20 mb-4">
                    <div className="flex justify-between items-start">
                        {/* Left side: Back and Start New buttons */}
                        <div className="flex gap-4">
                            <Button
                                variant="secondary"
                                onClick={handleBack}
                                disabled={activeStep === 0 || isNextLoading}
                            >
                                {t('converter.buttons.back')}
                            </Button>

                            {file && (
                                <Button
                                    variant="secondary"
                                    onClick={resetState}
                                    disabled={isNextLoading}
                                >
                                    {t('converter.buttons.startNew')}
                                </Button>
                            )}
                        </div>

                        {/* Right side: Next button */}
                        <div className="flex flex-col items-end">
                            {activeStep === 0 && (
                                <Button
                                    onClick={handleNext}
                                    disabled={!file || isNextLoading}
                                    isLoading={isNextLoading}
                                >
                                    {t('converter.buttons.next')}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mt-6 p-4 bg-[var(--error)]/10 border border-[var(--error)]/20 text-[var(--error)] rounded-lg text-center">
                        {error}
                    </div>
                )}
            </div>
            
            {/* SEO Content Section */}
            <div className="container mx-auto px-4">
                <SEOContent
                    title={t('seoContent.xrechnungViewer.title')}
                    description={t('seoContent.xrechnungViewer.description')}
                    features={t('seoContent.xrechnungViewer.features')}
                    benefits={t('seoContent.xrechnungViewer.benefits')}
                    howItWorks={t('seoContent.xrechnungViewer.howItWorks')}
                    faq={t('seoContent.xrechnungViewer.faq')}
                    currentTool="xrechnungViewer"
                />
            </div>
            
            <Footer />

            {/* Full-screen processing overlay */}
            {isNextLoading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <LoadingOverlay startTime={processingStartTime} isProcessing={isNextLoading} />
                </div>
            )}
        </div>
    );
};

export default SimpleXRechnungViewer;